import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ResultsLayout } from 'components/layout/ResultsLayout'
import { useSearchAndFilter } from 'components/SearchAndFilter/useSearchAndFilter'
import { useGetApplicationsQuery } from 'hooks/queries/Customers/useGetApplicationsQuery'
import { usePaginationWithApi } from 'hooks/usePaginationWithApi'
import { ApplicationItems } from './ApplicationItems'
import { CustomersV2FiltersModal, type CustomersV2Filters } from './CustomersV2FiltersModal'

interface CustomersListV2Props {
  AddButton: React.ReactElement
  onRefetch?: (refetch: () => void) => void
}

const INITIAL_FILTERS: CustomersV2Filters = {}

export const CustomersListV2 = ({ AddButton, onRefetch }: CustomersListV2Props) => {
  const { t } = useTranslation()

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, resetPage } =
    usePaginationWithApi()

  const {
    searchValue,
    appliedSearch,
    appliedFilters,
    hasSearchAndFilter,
    setSearchValue,
    setAppliedFilters,
    onResetAll,
  } = useSearchAndFilter('', INITIAL_FILTERS)

  const { data, isFetching, isError, refetch } = useGetApplicationsQuery({
    page,
    size: rowsPerPage,
    name: appliedSearch || undefined,
    statuses: appliedFilters?.statuses,
    types: appliedFilters?.types,
  })

  useEffect(() => {
    onRefetch?.(refetch)
  }, [refetch, onRefetch])

  const applications = data?.content ?? []
  const totalElements = data?.totalElements ?? 0

  return (
    <ResultsLayout
      testId="customers-v2-results"
      isLoading={isFetching}
      isError={isError}
      hasResults={!!applications.length}
      hasSearchAndFilter={hasSearchAndFilter}
      searchAndFilter={{
        search: {
          placeholder: t('customers.searchPlaceholder'),
          value: searchValue,
          onChange: value => {
            setSearchValue(value)
            resetPage()
          },
        },
        filters: [],
        onResetAll: () => {
          onResetAll()
          resetPage()
        },
        additionalActions: (
          <CustomersV2FiltersModal
            appliedFilters={appliedFilters}
            onApply={newFilters => {
              setAppliedFilters(prev => ({ ...prev, ...newFilters }))
              resetPage()
            }}
            onResetAll={() => {
              onResetAll()
              resetPage()
            }}
          />
        ),
      }}
      resetSearchAndFilters={() => {
        onResetAll()
        resetPage()
      }}
      refetchResults={refetch}
      emptyState={{
        title: t('embeddedCustomers.emptyStatus.headline'),
        description: t('embeddedCustomers.emptyStatus.descriptionAddDetails'),
        PrimaryButton: AddButton,
      }}
      pagination={{
        page,
        rowsPerPage,
        total: totalElements,
        onChangePage: handleChangePage,
        onChangeRowsPerPage: handleChangeRowsPerPage,
      }}
    >
      <ApplicationItems applications={applications} />
    </ResultsLayout>
  )
}
